import { PRINT_BLEED_SPINE_LINE_PT } from '@/lib/document/print-bleed'

type DoubleSpreadSpineBarAboveShapesProps = {
  bleedMargin: number
  trimHeight: number
  /**
   * Shared, snapped bindery seam X — same value the ruler "0" tick, dashed trim, gutter tint, and
   * margin guides all draw at (`center-canvas/hooks/use-spread-layout.ts`'s
   * `snappedSpreadOriginCanvasX`).
   */
  spineSnapCanvasX: number
  /** Current canvas zoom. Applied here explicitly rather than via an ancestor's CSS `transform`. */
  scale: number
  zIndex?: number
}

/**
 * Solid bindery spine line for a double spread. Mounted by
 * `center-canvas/components/screen-space-layers.tsx` as a sibling of the Visual layer, inside the
 * same offset wrapper whose own position already puts its origin at canvas doc-space (0,0) on
 * screen — but computes its own final screen pixels (`doc * scale`) rather than relying on a
 * `transform: scale()` ancestor to do it, the same technique `VisualLayer` itself uses.
 *
 * This line went through two earlier homes before landing here, each ruled out by direct
 * live observation, not merely reasoned through:
 *
 * - Originally inside `BleedFrame` (`double-spread-document.tsx`). `BleedFrame`'s own
 *   `position: relative` + `z-[1]` establishes a stacking context that caps everything inside it
 *   below any sibling outside that context regardless of an internal z-index, so the bar could
 *   never actually render on top of shapes or the dashed trim from in there.
 * - Then moved to sit alongside the dashed trim / margin guides (still descendants of
 *   `documentCanvasRef`'s own `transform: scale()` tree), and separately to its own dedicated
 *   `transform: scale()` wrapper matching the Text Linking overlay's pattern. Devtools confirmed,
 *   in both cases, that this element's own computed `left`/`width` was exactly centered on the
 *   shared seam, yet the painted line still rendered visibly off from that box at high zoom —
 *   a `transform: scale()` ancestor, wherever it sits, can rasterize a sub-pixel-positioned
 *   descendant at a resolution that doesn't match its computed CSS box implies (the same class of
 *   pixel-grid mismatch the `frontend-canvas` skill documents for non-scaling-stroke SVG content,
 *   here affecting a plain filled `<div>` instead). Computing the pixel values directly here, with
 *   no CSS transform anywhere between this element and its `inset-0` wrapper, removes the
 *   secondary scaling step entirely — there is nothing left for a rasterizer to disagree with.
 *
 * Positioned with an explicit `left: X - width/2` offset rather than `transform: translateX(-50%)`,
 * which resolves against the element's own rendered width and can round asymmetrically for a
 * sub-pixel-wide element, leaving the bar reading off-center from the seam it's meant to split
 * evenly.
 */
export function DoubleSpreadSpineBarAboveShapes({
  bleedMargin,
  trimHeight,
  spineSnapCanvasX,
  scale,
  zIndex = 10,
}: DoubleSpreadSpineBarAboveShapesProps) {
  if (trimHeight <= 0) {
    return null
  }

  const widthPx = PRINT_BLEED_SPINE_LINE_PT * scale

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: spineSnapCanvasX * scale - widthPx / 2,
        top: bleedMargin * scale,
        width: widthPx,
        height: trimHeight * scale,
        backgroundColor: 'var(--doc-trim-border)',
        zIndex,
      }}
    />
  )
}
